import { InAppState } from "../base/InAppState";
import template from "./RegionDetailTemplate.html";

import { MapView } from "../../core/MapView";
import { Gallery } from "../../core/Gallery";
import { DragScroll } from "../../core/DragScroll";
import { PlanState } from "../plan/PlanState";
import { share } from "../../core/Share";
import { WakeLock } from "../../core/WakeLock";
import { _lcs } from "../../i18n";

/**
 * RegionDetailState — one restaurant, sight or route (`/region/:kind/:id`).
 *
 * Full-bleed sub-page opened from the Region tab; back returns there. One state
 * for both kinds because a guest reads the same things either way — what it is,
 * where it is, when it is open, how to get hold of it — and the record is
 * flattened into one shape by core/Region.js before it ever reaches this
 * template. The only real difference is what the map draws: a pin for a place,
 * the track for a tour.
 *
 * Always fetched rather than read from the list cache: a guest may arrive here
 * by deep link or reload the page, and the detail payload carries the long
 * texts, the opening hours and (for a tour) the geometry that the list
 * deliberately leaves out.
 */
export class RegionDetailState extends InAppState
{
    static ROUTE = "/region/:kind/:id";
    static ID = "region-detail";

    showsBottomNav() { return false; }

    async enter()
    {
        const kind = this.routeParams.getParam( "kind" );
        const id = this.routeParams.getParam( "id" );

        if ( ( kind !== "place" && kind !== "tour" ) || !id )
        {
            this.app.router.redirect( "/region" );
            return;
        }

        const entry = await this.app.region.getDetail( kind, id );

        // A guest sent a link by a friend who stayed last month may be opening
        // a record that has since been archived.
        if ( !entry )
        {
            this.app.view.toast( _lcs( "Dieser Eintrag ist nicht mehr verfügbar." ), "info" );
            this.app.router.redirect( "/region" );
            return;
        }

        if ( this.app.stateManager.currentState !== this )
        {
            return;
        }

        this._entry = entry;

        this.renderScreen( template, {
            p : {
                ...entry,
                hasRoute : entry.latitude !== null && entry.longitude !== null,
                // Following a route on the map is the one moment a guest wants
                // the screen to stay on — offered where the browser can do it.
                canKeepAwake : entry.kind === "tour" && Array.isArray( entry.track ) &&
                    entry.track.length > 1 && WakeLock.isSupported()
            }
        } );

        this.bind();

        // Hero and photo rail carry data-gallery indices; the viewer picks them
        // up by delegation (see core/Gallery.js).
        this._gallery = Gallery.attach( this, entry.images );
        // Drag only: this rail sits in the middle of a page the wheel is
        // needed to scroll.
        DragScroll.attach( this, this.app.view.pageContent.querySelector( '[data-id="photos"]' ),
            { wheel : false } );

        // After the render, so the canvas has a size to be measured at.
        this._renderMap( entry );
    }

    async exit()
    {
        this._destroyMap();

        // Leaving the tour ends "keep the screen on" — never longer than the screen.
        if ( this._wakeLock )
        {
            this._wakeLock.release();
            this._wakeLock = null;
        }

        // The viewer lives in the device frame, not in the screen — the
        // shell's own cleanup would not reach it.
        if ( this._gallery )
        {
            this._gallery.destroy();
            this._gallery = null;
        }

        await super.exit();
    }

    bind()
    {
        const root = this.app.view.pageContent;

        this.addEventListener( root.querySelector( '[data-id="back"]' ), "click",
            () => this.app.router.redirect( "/region" ) );

        // Bound now, resolved later: the buttons are in the markup before
        // _renderMap has built anything for them to act on.
        MapView.bindControls( this, root, () => this._map );

        const route = root.querySelector( '[data-id="route"]' );
        if ( route )
        {
            this.addEventListener( route, "click", () => this._startRoute() );
        }

        const add = root.querySelector( '[data-id="add"]' );
        if ( add )
        {
            this.addEventListener( add, "click", () => this._addToPlan( add ) );
        }

        const shareBtn = root.querySelector( '[data-id="share"]' );
        if ( shareBtn )
        {
            this.addEventListener( shareBtn, "click", () => share( this.app, { title : this._entry.title } ) );
        }

        const wake = root.querySelector( '[data-id="wake"]' );
        if ( wake )
        {
            this.addEventListener( wake, "click", () => this._toggleWake( wake ) );
        }
    }

    /** "Bildschirm anlassen" on a tour: on until tapped again or the screen is left. */
    async _toggleWake( button )
    {
        if ( !this._wakeLock )
        {
            this._wakeLock = new WakeLock();
        }

        let on = false;
        if ( this._wakeLock.active )
        {
            this._wakeLock.release();
        }
        else
        {
            on = await this._wakeLock.request();
            if ( !on )
            {
                this.app.view.toast( _lcs( "Der Bildschirm kann gerade nicht angelassen werden." ), "info" );
            }
        }

        if ( this.isActive() )
        {
            button.setAttribute( "aria-pressed", String( on ) );
            button.querySelector( '[data-id="wake-state"]' ).textContent = on ? _lcs( "An" ) : _lcs( "Aus" );
        }
    }

    /**
     * Put this restaurant, sight or route in the guest's plan.
     *
     * The planner keeps gastronomy and sights apart (they are one table split
     * by type — see the API's model/Place), so the entry's kind comes from the
     * record rather than from the route's `place`/`tour`.
     */
    async _addToPlan( button )
    {
        const kind = this._entry.kind === "tour" ? "tour" : ( this._entry.type || "poi" );

        button.disabled = true;
        button.setAttribute( "aria-busy", "true" );

        const outcome = await this.app.planner.addFromDetail( kind, this._entry.id, {
            onNeedsPlan : () => PlanState.requestAdd( kind, this._entry.id )
        } );

        if ( outcome !== "redirected" )
        {
            button.disabled = false;
            button.removeAttribute( "aria-busy" );
        }
    }

    /**
     * Hand the destination to whatever the guest navigates with.
     *
     * A `geo:` URI is the platform-neutral way to say "here is a point" — the
     * phone opens whichever map app the guest actually uses, and we neither
     * choose a provider for them nor send them anywhere on our behalf. The
     * `?q=` tail is what makes iOS and Android show a label rather than bare
     * coordinates.
     */
    _startRoute()
    {
        const entry = this._entry;
        if ( !entry || entry.latitude === null )
        {
            return;
        }

        const point = entry.latitude + "," + entry.longitude;
        window.open( "geo:" + point + "?q=" + point + "(" + encodeURIComponent( entry.title || "" ) + ")", "_blank" );
    }

    /* ---------------------------------- Map ---------------------------------- */

    _renderMap( entry )
    {
        const root = this.app.view.pageContent;
        const figure = root.querySelector( '[data-id="map"]' );
        const canvas = root.querySelector( '[data-id="map-canvas"]' );
        const hint = root.querySelector( '[data-id="map-hint"]' );

        const hasTrack = Array.isArray( entry.track ) && entry.track.length > 1;
        const hasPoint = entry.latitude !== null && entry.longitude !== null;

        if ( !hasTrack && !hasPoint )
        {
            return;
        }

        if ( !MapView.isAvailable( this.app ) )
        {
            hint.textContent = _lcs( "Für diese Unterkunft ist keine Kartenquelle hinterlegt." );
            hint.hidden = false;
            return;
        }

        try
        {
            // Shown before the map is built: Leaflet measures its container on
            // creation, and one inside a `hidden` element measures 0×0.
            figure.hidden = false;

            this._map = new MapView( this.app, canvas );

            if ( hasTrack )
            {
                this._map.addTrack( entry.track );
            }
            if ( hasPoint )
            {
                // For a tour this pin is the trailhead — the bit a guest has to
                // get to before the track means anything.
                this._map.addPin( entry );
            }

            const house = this.app.accommodation?.address;
            if ( house )
            {
                this._map.addHere( house.latitude, house.longitude,
                    this.app.accommodation?.name || null );
            }

            this._map.fit();

            requestAnimationFrame( () =>
            {
                if ( this._map )
                {
                    this._map.invalidate();
                    this._map.fit();
                }
            } );
        }
        catch ( e )
        {
            console.error( e );
            this._destroyMap();
            figure.hidden = true;
            hint.textContent = _lcs( "Die Karte konnte nicht geladen werden." );
            hint.hidden = false;
        }
    }

    _destroyMap()
    {
        if ( this._map )
        {
            this._map.destroy();
            this._map = null;
        }
    }
}
